import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { clinicQueryKey } from '@/hooks/useClinicSettings';
import type { ClinicRow } from '@/hooks/useClinicSettings';
import type { Database } from '@/types/database.generated';
import {
  localPartIssue,
  normalizeEmailLocalPart,
  type EmailLocale,
  type OverridableTemplateKey,
} from '@/lib/clinicEmail/catalog';
import { isMissingRpcError } from '@/lib/clinicEmail/saveError';

export type ClinicEmailTemplateRow =
  Database['public']['Tables']['clinic_email_templates']['Row'];

export type LocalPartAvailability = {
  available: boolean;
  reason: 'reserved' | 'taken' | 'invalid' | null;
  normalized: string | null;
};

export const clinicEmailTemplatesQueryKey = (clinicId: string | null | undefined) =>
  ['clinic-email-templates', clinicId ?? null] as const;

export function useClinicEmailTemplates(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: clinicEmailTemplatesQueryKey(clinicId),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<ClinicEmailTemplateRow[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('clinic_email_templates')
        .select('*')
        .eq('clinic_id', clinicId);
      if (error) throw error;
      return (data ?? []) as ClinicEmailTemplateRow[];
    },
    staleTime: 15_000,
  });
}

export function useCheckEmailLocalPart(clinicId: string | null | undefined) {
  return useMutation({
    mutationFn: async (localPart: string): Promise<LocalPartAvailability> => {
      if (!clinicId) throw new Error('No active clinic');
      const { data, error } = await supabase.rpc('check_clinic_email_local_part_available', {
        p_clinic_id: clinicId,
        p_local_part: localPart,
      });
      if (error) throw error;
      const row = (data ?? {}) as Record<string, unknown>;
      const reason = row.reason;
      return {
        available: row.available === true,
        reason:
          reason === 'reserved' || reason === 'taken' || reason === 'invalid'
            ? reason
            : null,
        normalized: typeof row.normalized === 'string' ? row.normalized : null,
      };
    },
  });
}

function rpcLikeError(code: string, message: string): Error & { code: string } {
  const err = new Error(message) as Error & { code: string };
  err.code = code;
  return err;
}

async function updateClinicEmailColumn(
  clinicId: string,
  patch: Pick<ClinicRow, 'email_local_part'> | Pick<ClinicRow, 'email_signature_text' | 'email_signature_html'>,
): Promise<ClinicRow> {
  const { data, error } = await supabase
    .from('clinics')
    .update(patch)
    .eq('id', clinicId)
    .select('*')
    .single();
  if (error) throw error;
  return data as ClinicRow;
}

export function useSetClinicEmailLocalPart(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (localPart: string): Promise<ClinicRow> => {
      if (!clinicId) throw new Error('No active clinic');
      const { data, error } = await supabase.rpc('set_clinic_email_local_part', {
        p_clinic_id: clinicId,
        p_local_part: localPart,
      });
      if (error && isMissingRpcError(error)) {
        const issue = localPartIssue(localPart);
        switch (issue) {
          case 'reserved':
            throw rpcLikeError('22023', 'invalid-input: email local-part is reserved');
          case 'invalid':
          case 'too_long':
          case 'empty':
            throw rpcLikeError('22023', 'invalid-input: email local-part format');
          case null:
            return updateClinicEmailColumn(clinicId, {
              email_local_part: normalizeEmailLocalPart(localPart),
            });
          default: {
            const _exhaustive: never = issue;
            throw _exhaustive;
          }
        }
      }
      if (error) throw error;
      return data as ClinicRow;
    },
    onSuccess: (row) => {
      qc.setQueryData(clinicQueryKey(clinicId ?? null), row);
    },
  });
}

export function useSetClinicEmailSignature(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (signatureText: string): Promise<ClinicRow> => {
      if (!clinicId) throw new Error('No active clinic');
      const { data, error } = await supabase.rpc('set_clinic_email_signature', {
        p_clinic_id: clinicId,
        p_signature_text: signatureText,
        p_signature_html: null,
      });
      if (error && isMissingRpcError(error)) {
        return updateClinicEmailColumn(clinicId, {
          email_signature_text: signatureText.trim() || null,
          email_signature_html: null,
        });
      }
      if (error) throw error;
      return data as ClinicRow;
    },
    onSuccess: (row) => {
      qc.setQueryData(clinicQueryKey(clinicId ?? null), row);
    },
  });
}

export function useUpsertClinicEmailTemplate(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      templateKey: OverridableTemplateKey;
      locale: EmailLocale;
      subject: string;
      body: string;
    }): Promise<ClinicEmailTemplateRow> => {
      if (!clinicId) throw new Error('No active clinic');
      const { data, error } = await supabase.rpc('upsert_clinic_email_template', {
        p_clinic_id: clinicId,
        p_template_key: input.templateKey,
        p_locale: input.locale,
        p_subject: input.subject,
        p_body_html: input.body,
      });
      if (error) throw error;
      return data as ClinicEmailTemplateRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clinicEmailTemplatesQueryKey(clinicId) });
    },
  });
}

export function useResetClinicEmailTemplate(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      templateKey: OverridableTemplateKey;
      locale: EmailLocale;
    }): Promise<void> => {
      if (!clinicId) throw new Error('No active clinic');
      const { error } = await supabase.rpc('reset_clinic_email_template', {
        p_clinic_id: clinicId,
        p_template_key: input.templateKey,
        p_locale: input.locale,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clinicEmailTemplatesQueryKey(clinicId) });
    },
  });
}

export function useUpdateOwnEmailSignature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      membershipId: string;
      signatureText: string;
    }) => {
      const { data, error } = await supabase.rpc('update_own_email_signature', {
        p_membership_id: input.membershipId,
        p_signature_text: input.signatureText,
        p_signature_html: null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: ['clinic-users', 'detail', variables.membershipId] });
    },
  });
}
